import {ReactNode, useEffect, useState} from "react";
import {EncounterContext} from "@/encounter/EncounterHooks.ts";
import {PlayerViewPortProvider} from "@/encounter/viewport/PlayerViewPortProvider.tsx";
import {DungeonProvider} from "@/encounter/dungeon/DungeonProvider.tsx";
import {CombatantProvider} from "@/encounter/combatants/CombatantProvider.tsx";
import {TurnProvider} from "@/encounter/turns/TurnProvider.tsx";
import AreaEffectProvider from "@/encounter/effect/AreaEffectProvider.tsx";
import {getEncounterById} from "@/api/EncounterApi.ts";
import Encounter from "@/encounter/model/Encounter.ts";
import {useCampaign} from "@/campaigns/CampaignContextHooks.ts";
import DoorStateProvider from "@/encounter/doors/DoorStateProvider.tsx";
import EncounterLightProvider from "@/encounter/lights/EncounterLightProvider.tsx";

export function EncounterRuntimeProvider({encounterId, children}: {
    encounterId?: string;
    children: ReactNode;
}) {
    const {campaign} = useCampaign();
    const [encounter, setEncounter] = useState<Encounter>();
    const [error, setError] = useState<Error>();

    useEffect(() => {
        if (!encounterId) return;

        let active = true;
        void getEncounterById(encounterId)
            .then(loaded => {
                if (!active) return;
                if (loaded.campaignId !== campaign.id) throw new Error("Encounter belongs to another campaign");
                setEncounter(loaded);
            })
            .catch(cause => {
                if (!active) return;
                setEncounter(undefined);
                setError(Object.assign(new Error("Encounter konnte nicht geladen werden."), {cause}));
            });

        return () => {
            active = false;
        };
    }, [campaign.id, encounterId]);

    if (!encounterId) throw new Error("Kein Encounter ausgewählt.");
    if (error) throw error;
    if (!encounter) return null;

    return <EncounterContext.Provider value={encounter}>
        <PlayerViewPortProvider>
            <DungeonProvider>
                <DoorStateProvider>
                    <CombatantProvider>
                        <EncounterLightProvider>
                            <TurnProvider>
                                <AreaEffectProvider>
                                    {children}
                                </AreaEffectProvider>
                            </TurnProvider>
                        </EncounterLightProvider>
                    </CombatantProvider>
                </DoorStateProvider>
            </DungeonProvider>
        </PlayerViewPortProvider>
    </EncounterContext.Provider>;
}
